import React, { useState } from "react";
import { Box, Button, CircularProgress, Stack, Typography } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useTranslation } from "react-i18next";
import color from "../../../Constants/colors";
import { useWorkspaceColors } from "../../../Contexts/WorkspaceColorContext";
import { VoicesEngagePost } from "../../../Models/voicesOverview";
import { useVoicesEngage } from "../../../Query/useVoicesOverview";
import VoicesCard from "../Common/VoicesCard";
import { formatPostTime } from "../Posts/VoicesPostRows";

const PAGE_SIZE = 10;

const VoicesEngage: React.FC = () => {
  const { t } = useTranslation("voicesPosts");
  const { colors: workspace } = useWorkspaceColors();
  const [limit, setLimit] = useState(PAGE_SIZE);
  const { data: engage, isLoading, isFetching } = useVoicesEngage(limit);
  const remaining = engage ? engage.total - engage.posts.length : 0;

  const renderPost = (post: VoicesEngagePost) => (
    <Box
      key={post.id}
      sx={{ px: 2, py: 1.5, borderRadius: 2, border: `1px solid ${color.gray200}` }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, alignItems: "center" }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {post.target.name}
          </Typography>
          <Typography variant="caption">
            {[formatPostTime(post.posted_at), post.campaign_name].filter(Boolean).join(" · ")}
          </Typography>
        </Box>
        <Button
          size="small"
          endIcon={<OpenInNewIcon fontSize="small" />}
          disabled={!post.linkedin_url}
          onClick={() =>
            post.linkedin_url && window.open(post.linkedin_url, "_blank", "noopener,noreferrer")
          }
          sx={{ textTransform: "none", color: workspace.accent, whiteSpace: "nowrap" }}
        >
          {t("engage_open")}
        </Button>
      </Box>
      <Typography
        variant="body2"
        sx={{
          mt: 1,
          color: color.gray700,
          display: "-webkit-box",
          WebkitLineClamp: 3,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          whiteSpace: "pre-wrap",
        }}
      >
        {post.text}
      </Typography>
    </Box>
  );

  return (
    <VoicesCard
      title={t("engage_title")}
      subtitle={t("engage_subtitle", { days: engage?.days ?? 14 })}
    >
      {!isLoading && engage && !engage.posts.length ? (
        <Typography variant="body2" sx={{ color: color.gray500 }}>
          {t("engage_empty", { days: engage.days })}
        </Typography>
      ) : (
        <Stack spacing={1.5}>
          {engage?.posts.map(renderPost)}
          {remaining > 0 && (
            <Button
              disabled={isFetching}
              startIcon={
                isFetching ? (
                  <CircularProgress size={14} sx={{ color: workspace.accent }} />
                ) : null
              }
              onClick={() => setLimit(limit + PAGE_SIZE)}
              sx={{ alignSelf: "center", textTransform: "none", color: workspace.accent }}
            >
              {t("engage_more", { count: Math.min(remaining, PAGE_SIZE), total: remaining })}
            </Button>
          )}
        </Stack>
      )}
    </VoicesCard>
  );
};

export default VoicesEngage;
